import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const PhoneVerificationPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    phoneNumber: '',
    verificationCode: ''
  });
  const [errors, setErrors] = useState({
    phoneError: false,
    codeError: false
  });
  const [result, setResult] = useState({
    show: false,
    success: false,
    message: ''
  });
  const [countdown, setCountdown] = useState(0);
  
  // 从URL参数获取id
  const getUrlId = () => {
    const queryParams = new URLSearchParams(window.location.search);
    return queryParams.get('id');
  };
  
  useEffect(() => {
    let interval;
    if (countdown > 0) {
      interval = setInterval(() => {
        setCountdown(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [countdown]);

  const validatePhoneNumber = (phone) => {
    const regex = /^1[3-9]\d{9}$/;
    return regex.test(phone);
  };

  const validateVerificationCode = (code) => {
    const regex = /^\d{6}$/;
    return regex.test(code);
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData({ ...formData, [id]: value });
    
    // Reset error for this field
    if (errors[`${id}Error`]) {
      setErrors({ ...errors, [`${id}Error`]: false });
    }
  };

  const handleGetCode = async () => {
    const phone = formData.phoneNumber.trim();
    const postId = getUrlId(); // 从URL获取id
    
    if (!phone) {
      setErrors({ ...errors, phoneError: true });
      setResult({
        show: true,
        success: false,
        message: '手机号不能为空'
      });
      return;
    }
    
    if (!validatePhoneNumber(phone)) {
      setErrors({ ...errors, phoneError: true });
      setResult({
        show: true,
        success: false,
        message: '手机号格式不正确，应为11位有效号码'
      });
      return;
    }
    
    try {
      const response = await fetch('api/auth-logs/generate-sms-code', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: postId,
          phone: phone
        })
      });
      
      const data = await response.json();
      
      if (data.code === 0) {
        setCountdown(60);
        setResult({
          show: true,
          success: true,
          message: data.msg
        });
      } else {
        setResult({
          show: true,
          success: false,
          message: data.msg
        });
      }
      
      // Hide result after 5 seconds
      setTimeout(() => {
        setResult({ show: false, success: false, message: '' });
      }, 5000);
    } catch (error) {
      setResult({
        show: true,
        success: false,
        message: `请求失败：${error.message}`
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const phone = formData.phoneNumber.trim();
    const smsCode = formData.verificationCode.trim();
    const postId = getUrlId(); // 从URL获取id
    
    if (!phone || !validatePhoneNumber(phone)) {
      setErrors({ ...errors, phoneError: true });
      return;
    }
    
    if (!smsCode || !validateVerificationCode(smsCode)) {
      setErrors({ ...errors, codeError: true });
      return;
    }
    
    try {
      const response = await fetch(`api/auth-logs/verify-sms-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: postId,
          phone: phone,
          sms: smsCode
        })
      });
      
      const data = await response.json();
      
      if (data.code === 0) {
        setResult({
          show: true,
          success: true,
          message: data.msg
        });
        
        setTimeout(() => {
          // 跳转到完成页面时带上id参数
          navigate(`/mpbind/complete?id=${encodeURIComponent(postId)}`);
        }, 1500);
      } else {
        setResult({
          show: true,
          success: false,
          message: data.msg
        });
      }
    } catch (error) {
      setResult({
        show: true,
        success: false,
        message: `验证请求失败：${error.message}`
      });
    }
  };

  return (
    <div className="container">
      <div className="card">
        <div className="card-header">
        <img src="/logo.png" alt="泉州师范学院" className="header-logo" />
          <h1>统一身份认证系统——手机绑定</h1>
        </div>
        
        <div className="card-body">
          <form id="phoneForm" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="phoneNumber">手机号码</label>
              <div className="input-container">
                <input 
                  type="tel" 
                  id="phoneNumber" 
                  className="form-control" 
                  placeholder="请输入需绑定的手机号码"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                />
              </div>
              {errors.phoneError && (
                <div className="error" id="phoneError">手机号格式不正确，应为11位有效号码</div>
              )}
              <div className="hint">示例：13800138000</div>
            </div>
            
            <div className="form-group">
              <label htmlFor="verificationCode">验证码</label>
              <div className="code-container">
                <div className="input-container code-input">
                  <input 
                    type="text" 
                    id="verificationCode" 
                    className="form-control" 
                    placeholder="请输入验证码"
                    value={formData.verificationCode}
                    onChange={handleInputChange}
                  />
                </div>
                <button 
                  type="button" 
                  id="getCodeBtn" 
                  className={`btn get-code-btn ${countdown > 0 ? 'disabled' : ''}`}
                  onClick={handleGetCode}
                  disabled={countdown > 0}
                >
                  {countdown > 0 ? (
                    <><i className="fas fa-clock"></i> {countdown}秒后重发</>
                  ) : (
                    <> 获取验证码</>
                  )}
                </button>
              </div>
              {errors.codeError && (
                <div className="error" id="codeError">验证码格式不正确，应为6位数字</div>
              )}
            </div>
            
            <button type="submit" className="btn">
              <i className="fas fa-check-circle"></i> 验证手机号
            </button>
          </form>
          
          {result.show && (
            <div className={`result ${result.success ? 'success' : 'error-result'}`} id="result">
              <i className={result.success ? "fas fa-check-circle" : "fas fa-exclamation-circle"}></i>
              <h3>{result.success ? '验证成功！' : '验证失败'}</h3>
              <p>{result.message}</p>
            </div>
          )}
          
        </div>
      </div>
    </div>
  );
};

export default PhoneVerificationPage;